import { Grid } from '@mui/material';
import Spinner from 'components/shared/Spinner';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import useRequest from 'hooks/useRequest';
import PaymentMethodType from 'store/enums/PaymentMethodType';
import { PaymentFormValues } from 'store/types/FormValues';
import PaymentProviderService from 'services/api/PaymentProviderService';
import { CitiSession, CitiResponse } from 'store/types/payments/Citi';
import PaymentMethodFormProps, { PaymentMethodFormHandle } from 'store/types/payments/PaymentMethodFormProps';
import {
  CitiFieldValidityResult,
  CitiHostedFieldSelector,
  citiHostedFieldSelectors,
  citiValidityFieldNames,
  FieldValidationState,
  getIncompleteFieldMessage,
  getInvalidFieldMessage,
  getCitiFieldErrorMessage,
  initialFieldValidationState,
  PendingSubmit,
  cardFields,
  isSuccessStatus,
  isFieldsInErrorStatus,
  normalizeCitiValiditySelector,
  normalizeCardBrand,
} from 'util/Citi';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { loadScript } from 'util/Script';

import styles from './CitiPaymentMethodForm.module.scss';

declare global {
  interface Window {
    PaymentSession?: {
      configure: (config: any) => void;
      updateSessionFromForm: (paymentType: string) => void;
      onChange: (selector: any) => void;
      onValidityChange: (
        selectors: string[],
        callback: (selector: CitiHostedFieldSelector, result: CitiFieldValidityResult) => void
      ) => void;
    };
  }
}

const CitiPaymentMethodForm = React.forwardRef<PaymentMethodFormHandle, PaymentMethodFormProps>(
  ({ disabled = false, onMethodValidChange, type = PaymentMethodType.Card, subsidiaryId, onChange }, ref) => {
    const [ready, setReady] = useState(false);
    const [busy, setBusy] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [fieldValidation, setFieldValidation] = useState<FieldValidationState>(initialFieldValidationState);

    const onChangeRef = useRef(onChange);
    const pendingSubmitRef = useRef<PendingSubmit | null>(null);

    const citiSessionRequest = useMemo(
      () =>
        type !== PaymentMethodType.Card
          ? undefined
          : subsidiaryId
          ? () => PaymentProviderService.getCitiSession(subsidiaryId)
          : () => PaymentProviderService.getCitiSession(),
      [subsidiaryId, type]
    );

    const { data, loading } = useRequest<CitiSession>(citiSessionRequest);

    const allFieldsValid = useMemo(
      () => citiHostedFieldSelectors.every((selector) => fieldValidation[selector].isValid),
      [fieldValidation]
    );

    const getFieldError = (selector: CitiHostedFieldSelector): string | null => {
      const field = fieldValidation[selector];

      if (field.hasError && field.touched) {
        return field.message;
      }

      if (submitAttempted && !field.isValid) {
        if (field.isIncomplete) {
          return getIncompleteFieldMessage(selector);
        }

        return field.message || getInvalidFieldMessage(selector, field.errorReason ?? undefined);
      }

      return null;
    };

    const fieldErrors = {
      nameOnCard: getFieldError('#cardholder-name'),
      number: getFieldError('#card-number'),
      expiryMonth: getFieldError('#expiry-month'),
      expiryYear: getFieldError('#expiry-year'),
      securityCode: getFieldError('#security-code'),
    };

    useEffect(() => {
      if (type !== PaymentMethodType.Card) {
        setReady(true);
        setBusy(false);
      }
    }, [type]);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      onMethodValidChange(ready && !busy && !submitError);
    }, [ready, busy, submitError, onMethodValidChange]);

    useEffect(() => {
      if (!data?.sessionId || !data?.sessionJsUrl) {
        return;
      }

      let scriptElement: HTMLScriptElement | null = null;
      let isNewScript = false;
      let disposed = false;

      const configureHostedFields = async () => {
        try {
          const result = await loadScript(data.sessionJsUrl);

          scriptElement = result.script;
          isNewScript = result.isNew;

          if (disposed) {
            return;
          }

          if (!window.PaymentSession) {
            throw new Error('Citi PaymentSession was not available.');
          }

          const registerValidityChangeHandlers = () => {
            window.PaymentSession?.onValidityChange([...citiValidityFieldNames], (selector, result) => {
              if (disposed) {
                return;
              }

              const hostedSelector = normalizeCitiValiditySelector(selector);

              if (!hostedSelector) {
                console.warn('Unknown Citi validity selector', selector, result);
                return;
              }

              const hasError = Boolean(result.hasError || (!result.isValid && !result.isIncomplete));

              setFieldValidation((current) => ({
                ...current,
                [selector]: {
                  touched: true,
                  isValid: Boolean(result.isValid),
                  isIncomplete: Boolean(result.isIncomplete),
                  hasError,
                  errorReason: result.errorReason ?? null,
                  message: hasError ? getCitiFieldErrorMessage(selector, result) : null,
                },
              }));

              setSubmitError(null);
            });
          };

          window.PaymentSession.configure({
            session: data.sessionId,
            fields: {
              card: {
                number: `#${cardFields.number}`,
                securityCode: `#${cardFields.securityCode}`,
                expiryMonth: `#${cardFields.expiryMonth}`,
                expiryYear: `#${cardFields.expiryYear}`,
                nameOnCard: `#${cardFields.nameOnCard}`,
              },
            },
            frameEmbeddingMitigation: ['javascript', 'x-frame-options', 'csp'],
            callbacks: {
              initialized: (response: CitiResponse) => {
                if (disposed) {
                  return;
                }

                if (isSuccessStatus(response.status)) {
                  registerValidityChangeHandlers();

                  setReady(true);
                  setSubmitError(null);
                  return;
                }

                const message = response.message || 'Hosted fields failed to initialize.';

                setReady(false);
                setSubmitError(message);
              },

              formSessionUpdate: (response: CitiResponse) => {
                if (disposed) {
                  return;
                }

                setBusy(false);

                const pendingSubmit = pendingSubmitRef.current;
                pendingSubmitRef.current = null;

                if (isSuccessStatus(response.status) && response.session?.updateStatus === 'SUCCESS') {
                  const card = response.sourceOfFunds?.provided.card;

                  const paddedMonth = String(card?.expiry.month).padStart(2, '0');

                  const values: Partial<PaymentFormValues> = {
                    paymentMethodValid: true,
                    token: response.session.id ?? data.sessionId,
                    card: {
                      bin: null,
                      exp: `${paddedMonth}${card?.expiry.year}`,
                      hash: null,
                      number: card?.number ?? null,
                      type: normalizeCardBrand(card?.brand ?? '') ?? null,
                    },
                  };

                  onChangeRef.current?.(values);
                  pendingSubmit?.resolve(values);

                  return;
                }

                const message = isFieldsInErrorStatus(response.status)
                  ? 'Please check the highlighted card fields.'
                  : response.errors?.message || response.message || 'Payment details could not be saved.';

                setSubmitError(message);
                pendingSubmit?.reject(new Error(message));
              },
            },
            interaction: {
              displayControl: {
                formatCard: 'EMBOSSED',
                invalidFieldCharacters: 'REJECT',
              },
            },
          });
        } catch (err) {
          if (disposed) {
            return;
          }

          const message = err instanceof Error ? err.message : 'Failed to load Citi payment form.';

          setReady(false);
          setSubmitError(message);
        }
      };

      configureHostedFields();

      return () => {
        disposed = true;

        if (pendingSubmitRef.current) {
          pendingSubmitRef.current.reject(new Error('Payment form was unloaded before payment completed.'));
          pendingSubmitRef.current = null;
        }

        if (isNewScript && scriptElement?.parentNode) {
          scriptElement.parentNode.removeChild(scriptElement);
        }
      };
    }, [data?.sessionId, data?.sessionJsUrl]);

    useImperativeHandle(
      ref,
      () => ({
        finalizePaymentMethod: async () => {
          if (disabled) {
            throw new Error('Payment form is disabled.');
          }

          if (type !== PaymentMethodType.Card) {
            throw new Error('This payment method is not supported by Citi.');
          }

          if (!window.PaymentSession) {
            throw new Error('Citi payment session is not available.');
          }

          if (!ready) {
            throw new Error('Citi payment form is not ready.');
          }

          if (!allFieldsValid) {
            setSubmitAttempted(true);
            setSubmitError('Please complete all required card fields.');

            throw new Error('Please complete all required card fields.');
          }

          if (busy || pendingSubmitRef.current) {
            throw new Error('Payment submission is already in progress.');
          }

          setSubmitAttempted(true);
          setBusy(true);
          setSubmitError(null);

          return new Promise<Partial<PaymentFormValues>>((resolve, reject) => {
            pendingSubmitRef.current = { resolve, reject };

            try {
              window.PaymentSession?.updateSessionFromForm('card');
            } catch (err) {
              pendingSubmitRef.current = null;
              setBusy(false);

              reject(err instanceof Error ? err : new Error('Failed to update Citi payment session.'));
            }
          });
        },
      }),
      [disabled, type, ready, allFieldsValid, busy]
    );

    return (
      <Spinner loading={loading || !ready} transparent={false}>
        {disabled ? null : type === PaymentMethodType.Card ? (
          <Grid {...defaultGridContainerProps} spacing={1} className={styles.paymentForm}>
            <Grid {...defaultGridItemProps}>
              <div className={styles.formGroup}>
                <div className={`${styles.hostedField} ${fieldErrors.nameOnCard ? styles.hostedFieldError : ''}`}>
                  <label htmlFor={cardFields.nameOnCard} className={styles.inputLabel}>
                    Cardholder Name
                  </label>

                  <input
                    type="text"
                    id={cardFields.nameOnCard}
                    className={styles.inputField}
                    title="cardholder name"
                    aria-label="enter name on card"
                    aria-describedby={fieldErrors.nameOnCard ? `${cardFields.nameOnCard}-error` : undefined}
                    aria-invalid={Boolean(fieldErrors.nameOnCard)}
                    placeholder="Name"
                    tabIndex={1}
                    readOnly
                  />
                </div>

                {fieldErrors.nameOnCard && (
                  <div id={`${cardFields.nameOnCard}-error`} className={styles.errorMessage}>
                    {fieldErrors.nameOnCard}
                  </div>
                )}
              </div>
            </Grid>

            <Grid {...defaultGridItemProps}>
              <div className={styles.formGroup}>
                <div className={`${styles.hostedField} ${fieldErrors.number ? styles.hostedFieldError : ''}`}>
                  <label htmlFor={cardFields.number} className={styles.inputLabel}>
                    Card number
                  </label>

                  <input
                    type="text"
                    id={cardFields.number}
                    className={styles.inputField}
                    title="card number"
                    aria-label="enter your card number"
                    aria-describedby={fieldErrors.number ? `${cardFields.number}-error` : undefined}
                    aria-invalid={Boolean(fieldErrors.number)}
                    placeholder="0000 0000 0000 0000"
                    tabIndex={2}
                    readOnly
                  />
                </div>

                {fieldErrors.number && (
                  <div id={`${cardFields.number}-error`} className={styles.errorMessage}>
                    {fieldErrors.number}
                  </div>
                )}
              </div>
            </Grid>

            <Grid {...defaultGridItemProps} md={4}>
              <div className={styles.formGroup}>
                <div className={`${styles.hostedField} ${fieldErrors.expiryMonth ? styles.hostedFieldError : ''}`}>
                  <label htmlFor={cardFields.expiryMonth} className={styles.inputLabel}>
                    Expiration Month
                  </label>

                  <input
                    type="text"
                    id={cardFields.expiryMonth}
                    className={styles.inputField}
                    title="expiry month"
                    aria-label="two digit expiry month"
                    aria-describedby={fieldErrors.expiryMonth ? `${cardFields.expiryMonth}-error` : undefined}
                    aria-invalid={Boolean(fieldErrors.expiryMonth)}
                    placeholder="MM"
                    tabIndex={3}
                    readOnly
                  />
                </div>

                {fieldErrors.expiryMonth && (
                  <div id={`${cardFields.expiryMonth}-error`} className={styles.errorMessage}>
                    {fieldErrors.expiryMonth}
                  </div>
                )}
              </div>
            </Grid>

            <Grid {...defaultGridItemProps} md={4}>
              <div className={styles.formGroup}>
                <div className={`${styles.hostedField} ${fieldErrors.expiryYear ? styles.hostedFieldError : ''}`}>
                  <label htmlFor={cardFields.expiryYear} className={styles.inputLabel}>
                    Expiration Year
                  </label>

                  <input
                    type="text"
                    id={cardFields.expiryYear}
                    className={styles.inputField}
                    title="expiry year"
                    aria-label="two digit expiry year"
                    aria-describedby={fieldErrors.expiryYear ? `${cardFields.expiryYear}-error` : undefined}
                    aria-invalid={Boolean(fieldErrors.expiryYear)}
                    placeholder="YY"
                    tabIndex={4}
                    readOnly
                  />
                </div>

                {fieldErrors.expiryYear && (
                  <div id={`${cardFields.expiryYear}-error`} className={styles.errorMessage}>
                    {fieldErrors.expiryYear}
                  </div>
                )}
              </div>
            </Grid>

            <Grid {...defaultGridItemProps} md={4}>
              <div className={styles.formGroup}>
                <div className={`${styles.hostedField} ${fieldErrors.securityCode ? styles.hostedFieldError : ''}`}>
                  <label htmlFor={cardFields.securityCode} className={styles.inputLabel}>
                    Security code
                  </label>

                  <input
                    type="text"
                    id={cardFields.securityCode}
                    className={styles.inputField}
                    title="security code"
                    aria-label="three digit CCV security code"
                    aria-describedby={fieldErrors.securityCode ? `${cardFields.securityCode}-error` : undefined}
                    aria-invalid={Boolean(fieldErrors.securityCode)}
                    placeholder="123"
                    tabIndex={5}
                    readOnly
                  />
                </div>

                {fieldErrors.securityCode && (
                  <div id={`${cardFields.securityCode}-error`} className={styles.errorMessage}>
                    {fieldErrors.securityCode}
                  </div>
                )}
              </div>
            </Grid>

            {submitError && (
              <Grid {...defaultGridItemProps}>
                <div className={styles.errorMessage}>{submitError}</div>
              </Grid>
            )}
          </Grid>
        ) : (
          <>Payment method is not supported</>
        )}
      </Spinner>
    );
  }
);

CitiPaymentMethodForm.displayName = 'CitiPaymentMethodForm';

export default CitiPaymentMethodForm;
